import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  ChatBubbleOutline as ChatIcon,
  Reply as ReplyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { Feedback, FeedbackReply } from '../types';
import { useToast } from '../context/ToastContext';

const FeedbackSubmit: React.FC = () => {
  const [review, setReview] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useToast();
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingImagesLoading, setExistingImagesLoading] = useState(false);
  const [existingImagesError, setExistingImagesError] = useState('');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleImageError = (url: string) => {
    setImageErrors(prev => new Set(prev).add(url));
  };

  useEffect(() => {
    fetchFeedbacks();
    loadExistingImages();
  }, []);

  // Handle browser back button to close modals
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (imageDialogOpen) {
        setImageDialogOpen(false);
      } else if (galleryDialogOpen) {
        setGalleryDialogOpen(false);
      } else if (showImageModal) {
        setShowImageModal(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [imageDialogOpen, galleryDialogOpen, showImageModal]);

  const loadExistingImages = async () => {
    setExistingImagesLoading(true);
    setExistingImagesError('');
    try {
      // First, try to load from localStorage
      const storedFeedbackImages = localStorage.getItem('feedbackImages');
      if (storedFeedbackImages) {
        try {
          const parsedImages = JSON.parse(storedFeedbackImages);
          let imagesArray: string[] = [];
          
          // Handle both direct array and response format
          if (Array.isArray(parsedImages)) {
            imagesArray = parsedImages;
          } else if (parsedImages.data && Array.isArray(parsedImages.data)) {
            // If it's an array of objects with 'path' property
            imagesArray = parsedImages.data.map((img: { path: string } | string) => 
              typeof img === 'string' ? img : img.path
            );
          }
          
          // Use localStorage data even if empty array to avoid API call
          if (Array.isArray(imagesArray)) {
            setExistingImages(imagesArray);
            setExistingImagesLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to parse feedbackImages from localStorage:', error);
        }
      }
      
      // If not in localStorage or empty, fetch from API
      const response = await apiService.getImages('feedback');
      if (response.status && Array.isArray(response.data)) {
        const imagePaths = response.data.map(img => img.path);
        setExistingImages(imagePaths);
        // Store in localStorage for future use
        localStorage.setItem('feedbackImages', JSON.stringify(imagePaths));
      } else {
        setExistingImagesError(response.message || 'Failed to load images');
      }
    } catch (err) {
      setExistingImagesError('Failed to load images');
      console.error('Error loading images:', err);
    } finally {
      setExistingImagesLoading(false);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      setLoadingFeedbacks(true);
      setError(null);
      const response = await apiService.getFeedbacks();
      
      if (response.status && response.doc) {
        setFeedbacks(response.doc);
      } else {
        setError(response.message || 'Failed to fetch feedbacks');
      }
    } catch (err) {
      setError('An error occurred while fetching feedbacks');
      console.error('Error fetching feedbacks:', err);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const result = await apiService.uploadImage(file, 'feedback');
      
      if (result.status && result.data) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('feedbackImages');
        setImageUrls([...imageUrls, result.data.path]);
        setShowImageModal(false);
        // Refresh existing images list
        loadExistingImages();
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      showError('Upload Failed', 'Failed to upload image. Please try again.');
      console.error('Image upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageSelect = (imageUrl: string) => {
    if (!imageUrls.includes(imageUrl)) {
      setImageUrls([...imageUrls, imageUrl]);
    }
    setShowImageModal(false);
  };

  const handleImageDelete = async (imagePath: string) => {
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await apiService.deleteImage(imagePath);
      if (result.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('feedbackImages');
        // Remove from existing images
        setExistingImages(existingImages.filter(img => img !== imagePath));
        // Remove from selected images if it was selected
        setImageUrls(imageUrls.filter(img => img !== imagePath));
      } else {
        setError(result.message || 'Failed to delete image');
      }
    } catch (err) {
      setError('Failed to delete image');
      console.error('Image delete error:', err);
    }
  };

  const handleSubmit = async () => {
    if (!review.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.submitFeedback(review, imageUrls);
      
      if (response.status && response.doc) {
        setSuccess(true);
        setReview('');
        setImages([]);
        setImageUrls([]);
        // Refresh the feedback list to show the new submission
        fetchFeedbacks();
        // Reset success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
        console.log('Feedback submitted successfully:', response.doc);
      } else {
        setError(response.message || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('An error occurred while submitting feedback');
      console.error('Error submitting feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = () => {
    window.history.pushState({ modalOpen: true }, '');
    setImageDialogOpen(true);
  };

  const handleGalleryImageClick = (images: string[]) => {
    setSelectedImages(images);
    window.history.pushState({ modalOpen: true }, '');
    setGalleryDialogOpen(true);
  };

  const handleOpenImageModal = () => {
    window.history.pushState({ modalOpen: true }, '');
    setShowImageModal(true);
  };

  const handleCloseImageDialog = () => {
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    setImageDialogOpen(false);
  };

  const handleCloseGalleryDialog = () => {
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    setGalleryDialogOpen(false);
  };

  const handleCloseImageModal = () => {
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    setShowImageModal(false);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getInitials = (text: string) => {
    return text.charAt(0).toUpperCase();
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to bottom when feedbacks are loaded or updated
  useEffect(() => {
    if (!loadingFeedbacks) {
      scrollToBottom();
    }
  }, [feedbacks, loadingFeedbacks]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Help Center
      </Typography>

      {/* Success/Error Messages */}
      {success && (
        <Alert 
          severity="success" 
          icon={<SuccessIcon />}
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          Thank you for your message! Our support team will review it and get back to you soon.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Chat Area */}
      <Paper elevation={3} sx={{ height: { xs: '60vh', sm: '65vh', md: '70vh' }, display: 'flex', flexDirection: 'column' }}>
        {/* Chat Header */}
        <Box p={{ xs: 1, sm: 2 }} borderBottom="1px solid #e0e0e0">
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Support Chat
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Have questions or need help? Our support team is here to assist you.
          </Typography>
        </Box>

        {/* Messages Area */}
        <Box ref={messagesContainerRef} sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
          {loadingFeedbacks ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : feedbacks.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
              <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Welcome to Support Chat
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Send your first message below to start a conversation with our support team.
              </Typography>
            </Box>
          ) : (
            <Box>
              {feedbacks.map((feedback, index) => (
                <Box key={feedback._id} sx={{ mb: 3 }}>
                  {/* Customer Message */}
                  <Box display="flex" justifyContent="flex-start" mb={2}>
                    <Box display="flex" alignItems="flex-start" gap={{ xs: 1, sm: 2 }} maxWidth={{ xs: '95%', sm: '80%' }}>
                      <Avatar sx={{ bgcolor: '#1976d2', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        {getInitials(feedback.review)}
                      </Avatar>
                      <Box>
                        <Paper
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: '#f5f5f5',
                            borderRadius: '16px 16px 16px 4px',
                            maxWidth: '100%',
                          }}
                        >
                          <Typography variant="body1" sx={{ wordBreak: 'break-word', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {feedback.review}
                          </Typography>
                          {feedback.images.length > 0 && (
                            <Box mt={1}>
                              <Tooltip title="View images">
                                <IconButton
                                  size="small"
                                  onClick={() => handleGalleryImageClick(feedback.images)}
                                  color="primary"
                                >
                                  <Badge badgeContent={feedback.images.length} color="secondary">
                                    <ImageIcon />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Customer • {formatDate(feedback.createdAt)}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  </Box>

                  {/* Support Replies */}
                  {feedback.replies.map((replyObj, replyIndex) => (
                    <Box key={replyIndex} display="flex" justifyContent="flex-end" mb={2}>
                      <Box display="flex" alignItems="flex-start" gap={{ xs: 1, sm: 2 }} maxWidth={{ xs: '95%', sm: '80%' }}>
                        <Box>
                          <Paper
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              bgcolor: '#e3f2fd',
                              borderRadius: '16px 16px 4px 16px',
                              maxWidth: '100%',
                            }}
                          >
                            <Typography variant="body1" sx={{ wordBreak: 'break-word', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {replyObj.reply}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              Support Team • {formatDate(replyObj.createdAt)}
                            </Typography>
                          </Paper>
                        </Box>
                        <Avatar sx={{ bgcolor: '#4caf50', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          S
                        </Avatar>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Divider />

        {/* Chat Input */}
        <Box p={{ xs: 1, sm: 2 }}>
          {/* Image Preview */}
          {imageUrls.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Attached Images:
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {imageUrls.map((url, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    {imageErrors.has(url) ? (
                      <Box
                        sx={{
                          width: { xs: 50, sm: 60 },
                          height: { xs: 50, sm: 60 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', textAlign: 'center', px: 0.5 }}>
                          Expired
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={url}
                        alt={`Upload ${index + 1}`}
                        onError={() => handleImageError(url)}
                        sx={{
                          width: { xs: 50, sm: 60 },
                          height: { xs: 50, sm: 60 },
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={handleImageClick}
                      />
                    )}
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        width: 20,
                        height: 20,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(index);
                      }}
                      disabled={loading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Input Area */}
          <Box display="flex" gap={{ xs: 0.5, sm: 1 }} alignItems="flex-end">
            {/* Image Upload Button */}
            <IconButton
              onClick={handleOpenImageModal}
              disabled={loading}
              color="primary"
              size="small"
            >
              <ImageIcon fontSize="small" />
            </IconButton>

            {/* Text Input */}
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message here..."
              value={review}
              onChange={(e) => setReview(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              disabled={loading}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />

            {/* Send Button */}
            <IconButton
              onClick={handleSubmit}
              disabled={loading || !review.trim()}
              color="primary"
              size="small"
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' }
              }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Image Gallery Dialog for Submission */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1.5,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Uploaded Images
          </Typography>
          <IconButton
            onClick={handleCloseImageDialog}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, '&.MuiDialogContent-root': { pt: 2 } }}>
          <Grid container spacing={2}>
            {imageUrls.map((url, index) => (
              <Grid item xs={6} sm={4} key={index}>
                {imageErrors.has(url) ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 150,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Expired
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={url}
                    alt={`Feedback image ${index + 1}`}
                    onError={() => handleImageError(url)}
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => window.open(url, '_blank')}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCloseImageDialog}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Gallery Dialog for Feedback History */}
      <Dialog
        open={galleryDialogOpen}
        onClose={handleCloseGalleryDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1.5,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Feedback Images
          </Typography>
          <IconButton
            onClick={handleCloseGalleryDialog}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, '&.MuiDialogContent-root': { pt: 2 } }}>
          <Grid container spacing={2}>
            {selectedImages.map((url, index) => (
              <Grid item xs={6} sm={4} key={index}>
                {imageErrors.has(url) ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 150,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Expired
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={url}
                    alt={`Feedback image ${index + 1}`}
                    onError={() => handleImageError(url)}
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => window.open(url, '_blank')}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCloseGalleryDialog}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Selection Modal */}
      <Dialog
        open={showImageModal}
        onClose={handleCloseImageModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1.5,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Select or Upload Images
          </Typography>
          <IconButton
            onClick={handleCloseImageModal}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, '&.MuiDialogContent-root': { pt: 2 } }}>
          {/* Upload Section */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
              Upload New Image
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={handleImageUpload}
              disabled={uploading}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={uploading}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.dark' }
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
            </label>
          </Box>

          {/* Existing Images Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
              Select Existing Images
            </Typography>
            
            {existingImagesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading images...
                </Typography>
              </Box>
            ) : existingImagesError ? (
              <Alert severity="error" sx={{ mb: 2 }}>{existingImagesError}</Alert>
            ) : existingImages.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No existing images found. Upload a new image to get started.
              </Alert>
            ) : (
              <Box
                sx={{
                  maxHeight: '50vh',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      background: '#555',
                    },
                  },
                }}
              >
                <Grid container spacing={1.5}>
                  {existingImages.map((image, index) => (
                    <Grid item xs={4} sm={3} key={index}>
                      <Card
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: imageUrls.includes(image) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          borderRadius: 1,
                          overflow: 'hidden',
                          transition: 'all 0.2s',
                          '&:hover': { 
                            boxShadow: 3,
                            transform: 'translateY(-2px)',
                            borderColor: imageUrls.includes(image) ? '#1976d2' : '#bdbdbd',
                          },
                        }}
                        onClick={() => handleImageSelect(image)}
                      >
                        {imageErrors.has(image) ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: 100,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.200',
                              border: '1px solid',
                              borderColor: 'grey.300',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Expired
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            component="img"
                            src={image}
                            alt={`Image ${index + 1}`}
                            onError={() => handleImageError(image)}
                            sx={{
                              width: '100%',
                              height: 100,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        )}
                        
                        {/* Selection indicator */}
                        {imageUrls.includes(image) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: '#1976d2',
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                          >
                            ✓
                          </Box>
                        )}
                        
                        {/* Delete button */}
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            width: 24,
                            height: 24,
                            '&:hover': { 
                              bgcolor: 'rgba(211, 47, 47, 0.8)',
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageDelete(image);
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCloseImageModal}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbackSubmit;
